import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { stockLotsApi } from './service';
import type { StockLotCreateValues, StockLotUpdateValues } from './schema';
import type { StockLot, StockLotFilters } from './types';

/**
 * Query key factory parameterised by filters so different filter
 * combinations stay in cache without invalidating each other.
 *
 *   stockLotsKeys.all              every cached stock-lot query
 *   stockLotsKeys.lists()          all list queries
 *   stockLotsKeys.list(filters)    one list with specific filters
 *   stockLotsKeys.detail(id)       a single stock lot
 */
export const stockLotsKeys = {
  all: ['stock-lots'] as const,
  lists: () => [...stockLotsKeys.all, 'list'] as const,
  list: (filters: StockLotFilters = {}) =>
    [...stockLotsKeys.lists(), filters] as const,
  detail: (id: string) => [...stockLotsKeys.all, 'detail', id] as const,
};

export function useStockLots(filters: StockLotFilters = {}) {
  return useQuery({
    queryKey: stockLotsKeys.list(filters),
    queryFn: () => stockLotsApi.list(filters),
  });
}

export function useStockLot(id: string | undefined) {
  return useQuery({
    queryKey: id ? stockLotsKeys.detail(id) : stockLotsKeys.all,
    queryFn: () => stockLotsApi.getById(id as string),
    enabled: Boolean(id),
  });
}

export function useCreateStockLot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: StockLotCreateValues) => stockLotsApi.create(input),
    onSuccess: (created: StockLot) => {
      qc.invalidateQueries({ queryKey: stockLotsKeys.lists() });
      qc.setQueryData(stockLotsKeys.detail(created.id), created);
    },
  });
}

export function useUpdateStockLot(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: StockLotUpdateValues) => stockLotsApi.update(id, patch),
    onSuccess: (updated: StockLot) => {
      qc.invalidateQueries({ queryKey: stockLotsKeys.lists() });
      qc.setQueryData(stockLotsKeys.detail(id), updated);
    },
  });
}

export function useDeleteStockLot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => stockLotsApi.remove(id),
    onSuccess: (_void, id) => {
      qc.invalidateQueries({ queryKey: stockLotsKeys.lists() });
      qc.removeQueries({ queryKey: stockLotsKeys.detail(id) });
    },
  });
}
